import { useCallback, useContext, useMemo } from 'react';
import { AxiosResponse } from 'axios';
import { BookingDataContext } from '../../../context/bookingDataContext/bookingDataContext';
import { BookingConfigEnum } from '../../BookingWidget/bookingSteps.interface';
import { BookingConfigContext } from '../../../context/bookingConfigContext/bookingConfigContext';
import useApi from '../../../shared/services/api.service';
import { NotificationContext } from '../../../shared/components/Notification/NotificationContext';
import { LoadingContext } from '../../../context/loadingContext/loadingContext';
import { useSessionContext } from '../../../context/sessionContext/sessionContext';

interface UseFinishMedicalScreening {
  isMedicalScreeningComplete: boolean;
  finishMedicalScreening: (isLoadingHandledOutside?: boolean) => Promise<void>;
}

const useFinishMedicalScreening = (): UseFinishMedicalScreening => {
  const { bookingDataResponse, updateBookingData } =
    useContext(BookingDataContext);
  const { showNotification } = useContext(NotificationContext);
  const { setIsLoading } = useContext(LoadingContext);
  const { bookingConfigData } = useContext(BookingConfigContext);
  const { sessionParams } = useSessionContext();
  const API = useApi(bookingConfigData, sessionParams);
  const apiCM360Url: string =
    bookingConfigData?.[BookingConfigEnum.DataCenter]?.cm360Endpoint;

  const handleLoading = (
    isLoadingHandledOutside: boolean,
    isLoadingState?: boolean,
  ): void => {
    if (!isLoadingHandledOutside && isLoadingState !== undefined) {
      setIsLoading(isLoadingState);
    }
  };

  const finishMedicalScreening = useCallback(
    async (isLoadingHandledOutside = false) => {
      handleLoading(isLoadingHandledOutside, true);
      const url = `${apiCM360Url}/pemc-screenings/${bookingDataResponse?.bookingData?.id}`;
      const response: AxiosResponse<{ pemcScreeningId: string }> =
        await API.post(url);
      if (!response.data?.pemcScreeningId) {
        handleLoading(isLoadingHandledOutside, false);
        showNotification('unexpectedError', 'error', false);
        return;
      }

      updateBookingData({
        ...bookingDataResponse!,
        bookingData: {
          ...bookingDataResponse!.bookingData!,
          pemcScreeningId: response.data.pemcScreeningId,
        },
      });
      handleLoading(isLoadingHandledOutside, false);
    },
    [apiCM360Url, bookingDataResponse],
  );

  const isMedicalScreeningComplete = useMemo(
    () => !!bookingDataResponse?.bookingData.pemcScreeningId,
    [bookingDataResponse],
  );

  return { isMedicalScreeningComplete, finishMedicalScreening };
};

export default useFinishMedicalScreening;
